import { useState } from "react";
import {
  confirmUpload,
  getPresignedUpload,
} from "../../api/services/uploadService";
import {
  submitRenewalResponse,
  type RenewalIntent,
} from "../../api/services/renewalResponseService";
import { useRenewal } from "./RenewalContext";
import { isRenewalTestMode } from "./renewalTestMode";

/**
 * Submits the concierge questionnaire: uploads any condition photos, POSTs
 * the answers, then advances to the confirmation screen.
 *
 * Photos go through the standard presigned-PUT flow (presign → PUT to S3 →
 * confirm), and only their upload UUIDs are sent with the response — same
 * shape the claim documents use.
 *
 * The API stores every submission as a new version, so a tenant who runs the
 * flow again doesn't overwrite what they said before.
 *
 * In test mode (`/renewal/test`) nothing is sent — the click-through has to
 * work without a session.
 */
export function useRenewalSubmit(onDone: () => void) {
  const { termUuid, draft } = useRenewal();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (intent: RenewalIntent) => {
    if (submitting) return;
    setError(null);

    if (isRenewalTestMode(termUuid)) {
      onDone();
      return;
    }

    setSubmitting(true);
    try {
      const documentUuids = await uploadFiles(draft.condition.files);

      await submitRenewalResponse(termUuid, {
        intent,
        preference: draft.preference,
        moveOutDate: draft.moveOutDate || null,
        hasDamage: draft.condition.hasDamage,
        conditionDescription: draft.condition.description || null,
        hasNotes: draft.notes.hasNotes,
        notes: draft.notes.text || null,
        contactName: draft.contact.name || null,
        contactEmail: draft.contact.email || null,
        contactPhone: draft.contact.phone || null,
        documentUuids,
        paymentMethodId: draft.paymentMethodId,
      });

      onDone();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "We couldn't submit your response. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return { submit, submitting, error };
}

/**
 * Presign → PUT → confirm for each file, returning the upload UUIDs.
 *
 * Sequential rather than parallel: a tenant attaches a handful of phone
 * photos at most, and serialising keeps the failure point obvious if one
 * upload dies mid-way.
 */
async function uploadFiles(files: File[]): Promise<string[]> {
  const uuids: string[] = [];
  for (const file of files) {
    const { data } = await getPresignedUpload(file.name);
    const putResponse = await fetch(data.presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!putResponse.ok) {
      throw new Error(`Failed to upload ${file.name}`);
    }
    await confirmUpload(data.uuid);
    uuids.push(data.uuid);
  }
  return uuids;
}
